/**
 * [T-052] AI assist contracts (W4-CONTRACTS §3) — รูปร่างผลลัพธ์ของแต่ละ feature ผ่าน aiGateway
 * ทุกอย่างเป็น "suggestion ที่คนยืนยัน" — ไม่มีผล AI ใดส่งออกไปลูกค้าโดยตรง
 */
import { z } from "zod";

/** Routing classifier (11 §2) — ผลลัพธ์จากโมเดล ผ่าน safeParse ก่อนใช้เสมอ */
export const AiRoutingResult = z.object({
  team_code: z.string().min(1).max(40),
  category: z.string().min(1).max(80),
  tags: z.array(z.string().min(1).max(64)).max(10).default([]),
  priority: z.enum(["low", "normal", "high", "urgent"]).nullable().default(null),
  language: z.string().min(2).max(8).nullable().default(null),
  confidence: z.number().min(0).max(1),
  reasons: z.array(z.string().min(1).max(300)).max(8).default([]),
});
export type AiRoutingResult = z.infer<typeof AiRoutingResult>;

/** Sentiment & urgency ต่อข้อความลูกค้า (automation Field ai.sentiment / ai.urgency) */
export const AiSentimentResult = z.object({
  sentiment: z.enum(["neg", "neu", "pos"]),
  urgency: z.enum(["low", "med", "high"]),
  frustration_signals: z.array(z.string().min(1).max(80)).max(10).default([]),
});
export type AiSentimentResult = z.infer<typeof AiSentimentResult>;

/** Language detect — heuristic ก่อน ไม่ชัดค่อยใช้โมเดล */
export const AiLanguageResult = z.object({ lang: z.string().min(2).max(8) });
export type AiLanguageResult = z.infer<typeof AiLanguageResult>;

/** Summary — bullets + สิ่งที่รอ + ขั้นถัดไป (cache KV 10 นาทีต่อ (ticket, version)) */
export const AiSummary = z.object({
  bullets: z.array(z.string().min(1).max(500)).min(1).max(6),
  waiting_on: z.string().max(500).nullable().default(null),
  next_steps: z.array(z.string().min(1).max(300)).max(6).default([]),
});
export type AiSummary = z.infer<typeof AiSummary>;
export const AiSummaryInput = z.object({ lang: z.string().min(2).max(8).optional() });
export type AiSummaryInput = z.infer<typeof AiSummaryInput>;

/** Draft reply (RAG) — คืนพร้อม kb_refs ที่อ้าง */
export const AiDraft = z.object({
  html: z.string().min(1).max(100_000),
  kb_refs: z
    .array(
      z.object({
        article_id: z.string().min(1),
        title: z.string().min(1).max(300),
        url: z.string().max(1000).nullable().default(null),
      }),
    )
    .max(10)
    .default([]),
});
export type AiDraft = z.infer<typeof AiDraft>;
export const AiDraftInput = z.object({
  tone: z.enum(["polite", "short", "formal"]).optional(),
  lang: z.string().min(2).max(8).optional(),
  from_bullets: z.array(z.string().min(1).max(500)).max(10).optional(),
});
export type AiDraftInput = z.infer<typeof AiDraftInput>;

/** Translate — เก็บ cache ใน message_translations (unique message_id+lang) */
export const AiTranslation = z.object({
  message_id: z.uuid(),
  lang: z.string().min(2).max(8),
  body_html: z.string(),
  body_text: z.string(),
  provider: z.string(),
  model: z.string(),
  cached: z.boolean().default(false),
});
export type AiTranslation = z.infer<typeof AiTranslation>;
export const AiTranslateInput = z.object({ lang: z.string().min(2).max(8) });
export type AiTranslateInput = z.infer<typeof AiTranslateInput>;

/** PII NER hints — spans บน body_text ของข้อความ (คนเลือกเสมอ — ไม่ใช่ redaction อัตโนมัติ) */
export const AiPiiHint = z.object({
  message_id: z.uuid(),
  span_start: z.number().int().min(0),
  span_end: z.number().int().min(1),
  category: z.string().min(1).max(40),
  text: z.string().max(200),
  suggested_replacement: z.string().max(80),
});
export type AiPiiHint = z.infer<typeof AiPiiHint>;

/** Field extraction — suggestion เท่านั้น ไม่เขียน custom_fields เอง */
export const AiExtractResult = z.object({
  fields: z.record(z.string(), z.object({ value: z.unknown(), confidence: z.number().min(0).max(1) })),
});
export type AiExtractResult = z.infer<typeof AiExtractResult>;

/** Article draft จากข้อความ (ลบชื่อ/ข้อมูลลูกค้า) */
export const AiKbDraft = z.object({
  title: z.string().min(1).max(200),
  body_html: z.string().min(1).max(100_000),
  tags: z.array(z.string().min(1).max(64)).max(10).default([]),
});
export type AiKbDraft = z.infer<typeof AiKbDraft>;

/** admin log — แถว ai_runs (T-055 ใช้ในหน้า AI settings) */
export const AiRun = z.object({
  id: z.number().int(),
  feature: z.string(),
  provider: z.string(),
  model: z.string(),
  prompt_version: z.string(),
  ticket_id: z.uuid().nullable(),
  message_id: z.uuid().nullable(),
  user_id: z.uuid().nullable(),
  input_tokens: z.number().int().nullable(),
  output_tokens: z.number().int().nullable(),
  cost_estimate: z.string().nullable(),
  latency_ms: z.number().int().nullable(),
  status: z.enum(["ok", "error", "budget_exceeded", "skipped"]),
  result: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  created_at: z.iso.datetime(),
});
export type AiRun = z.infer<typeof AiRun>;

/** app_settings.ai (T-054 เติม zod key ใน admin — T-052 อ่านผ่าน readSetting เท่านั้น) */
export const AiFeatureSettings = z.object({
  model: z.string().optional(),
  threshold: z.number().optional(),
  budget_usd_month: z.number().optional(),
  enabled: z.boolean().optional(),
});
export type AiFeatureSettings = z.infer<typeof AiFeatureSettings>;
